import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import backendClient from '../../services/backendClient';

function Testing() {
  const { t } = useTranslation('common');
  const [credits, setCredits] = useState(0);
  const [originalCredits, setOriginalCredits] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const hasChanges = credits !== originalCredits;

  useEffect(() => {
    fetchCredits();
  }, []);

  const fetchCredits = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await backendClient.get('/api/meal-plan-limit/check');
      const currentCredits = data.triesLeft || 0;
      setCredits(currentCredits);
      setOriginalCredits(currentCredits);
    } catch (err) {
      setError('Failed to fetch credits');
      console.error('Error fetching credits:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIncrement = () => {
    setCredits(prev => prev + 1);
    setSuccessMessage(null);
  };

  const handleDecrement = () => {
    setCredits(prev => Math.max(0, prev - 1));
    setSuccessMessage(null);
  };

  const handleInputChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0) {
      setCredits(value);
      setSuccessMessage(null);
    } else if (e.target.value === '') {
      setCredits(0);
      setSuccessMessage(null);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);
      await backendClient.put('/api/meal-plan-limit/set-credits', { credits });
      setOriginalCredits(credits);
      setSuccessMessage('Credits saved successfully!');
    } catch (err) {
      setError('Failed to save credits');
      console.error('Error saving credits:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#fff] pt-[60px]">
      <div className="max-w-lg mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-[#0d0c07] text-[28px] font-bold leading-[40px] mb-2">
            🧪 Testing Panel
          </h1>
          <p className="text-[#56554D] text-[16px] leading-[24px]">
            Manage user credits for testing purposes
          </p>
        </div>

        {/* Credits Card */}
        <div className="bg-white rounded-[16px] p-6 shadow-[0px_1px_2px_0px_rgba(35,33,22,0.02),4px_12px_20px_0px_rgba(83,81,69,0.12)] border-[0.5px] border-[#e2e1df]">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#006600]"></div>
            </div>
          ) : (
            <>
              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              {/* Success Message */}
              {successMessage && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
                  {successMessage}
                </div>
              )}

              {/* Credits Display */}
              <div className="mb-6">
                <label className="block text-[#56554D] text-[14px] font-medium mb-2">
                  Current Credits
                </label>
                <div className="flex items-center justify-center gap-3">
                  {/* Decrement Button */}
                  <button
                    onClick={handleDecrement}
                    disabled={credits <= 0}
                    className="
                      w-12 h-12 rounded-full
                      bg-[#f5f5f4] hover:bg-[#e7e7e6]
                      disabled:opacity-50 disabled:cursor-not-allowed
                      flex items-center justify-center
                      text-[#0d0c07] text-[24px] font-bold
                      transition-colors
                      border border-[#e2e1df]
                    "
                  >
                    −
                  </button>

                  {/* Credits Input */}
                  <input
                    type="number"
                    value={credits}
                    onChange={handleInputChange}
                    min="0"
                    className="
                      flex-1 min-w-0 max-w-[120px] h-12 px-2
                      text-center text-[24px] font-bold text-[#0d0c07]
                      bg-[#f5f5f4] rounded-lg
                      border border-[#e2e1df]
                      focus:outline-none focus:ring-2 focus:ring-[#006600] focus:border-transparent
                      [appearance:textfield]
                      [&::-webkit-outer-spin-button]:appearance-none
                      [&::-webkit-inner-spin-button]:appearance-none
                    "
                  />

                  {/* Increment Button */}
                  <button
                    onClick={handleIncrement}
                    className="
                      w-12 h-12 rounded-full
                      bg-[#f5f5f4] hover:bg-[#e7e7e6]
                      flex items-center justify-center
                      text-[#0d0c07] text-[24px] font-bold
                      transition-colors
                      border border-[#e2e1df]
                    "
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Original Value Reference */}
              <div className="mb-6 text-center text-[#65635D] text-[14px]">
                Original: {originalCredits} credits
              </div>

              {/* Save Button - Only show when there are changes */}
              {hasChanges && (
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="
                    w-full h-[48px] px-6
                    rounded-[32px]
                    bg-[#006600] hover:bg-[#005500]
                    disabled:opacity-50 disabled:cursor-not-allowed
                    text-white text-[16px] font-semibold
                    transition-colors
                    flex items-center justify-center gap-2
                  "
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      💾 Save Changes
                    </>
                  )}
                </button>
              )}

              {/* Refresh Button */}
              <button
                onClick={fetchCredits}
                className="
                  w-full h-[40px] px-4 mt-4
                  rounded-lg
                  bg-transparent hover:bg-[#f5f5f4]
                  text-[#56554D] text-[14px] font-medium
                  transition-colors
                  border border-[#e2e1df]
                "
              >
                🔄 Refresh from Database
              </button>
            </>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-6 p-4 bg-[#fffce8] rounded-lg border border-[#ffe066]">
          <p className="text-[#65635D] text-[13px] leading-[20px]">
            <strong>Note:</strong> This page is for testing purposes only. 
            Changes made here will directly affect the user's credit balance in the database.
          </p>
        </div>
      </div>
    </main>
  );
}

export default Testing;
